import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Club, Event } from '../types';
import { isClubAdmin, isSuperAdmin } from '../utils/roleUtils';
import { CloseIcon, EditIcon, CalendarIcon, FolderIcon, SaveIcon } from './Icons';
import { uploadImageToCdn } from '../lib/uploadImage';
import { useWebSocket } from '../hooks/useWebSocket';
import CurvedSlider from './CurvedSlider';
import '../styles/ClubTemplate.css';

// Regular throttle function (NOT a hook)
const throttle = (callback: Function, delay: number) => {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      callback(...args);
    }
  };
};

const API_URL = BACKEND_URL;

interface ClubChannel {
  id: string;
  name: string;
  createdBy?: string;
  members: number;
  online?: number;
}

interface ClubChannelMessage {
  id: string;
  senderName: string;
  content: string;
  createdAt?: string;
}

interface ClubChannelMember {
  id: string;
  username: string;
  avatar?: string;
}

interface ChannelOnlineUser {
  id: string;
  username: string;
  lastSeen?: number;
}

const ClubTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { subscribe } = useWebSocket();
  const [club, setClub] = useState<Club | null>(null);
  const [clubError, setClubError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const presenceIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const paginationPageRef = useRef<number>(0);
  const isLoadingMoreMessagesRef = useRef<boolean>(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'events' | 'about' | 'community'>(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    return tab === 'community' ? 'community' : 'events';
  });
  const [clubEvents, setClubEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelActionLoading, setChannelActionLoading] = useState(false);
  const [clubChannels, setClubChannels] = useState<ClubChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [channelMessages, setChannelMessages] = useState<ClubChannelMessage[]>([]);
  const [channelMembers, setChannelMembers] = useState<ClubChannelMember[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<ChannelOnlineUser[]>([]);
  const [joinedChannelIds, setJoinedChannelIds] = useState<string[]>([]);
  const [newChannelName, setNewChannelName] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [communityStatus, setCommunityStatus] = useState('');

  // Form state
  const [title, setTitle] = useState('');
  const [facultyMembers, setFacultyMembers] = useState<string[]>([]);
  const [newFaculty, setNewFaculty] = useState('');
  const [gallery, setGallery] = useState<string[]>([]);

  // Check if current user can edit
  const canEdit = user && club && (
    isClubAdmin(user.role) && club.created_by === user.id ||
    isSuperAdmin(user.role)
  );

  const currentUsername = user?.username || 'Student';
  const selectedChannel = clubChannels.find((channel) => channel.id === selectedChannelId) || null;
  const isInSelectedChannel = !!selectedChannelId && joinedChannelIds.includes(selectedChannelId);

  const buildConfig = (needsAuth: boolean = false) => {
    if (needsAuth && token) {
      return { headers: { Authorization: `Bearer ${token}` }, timeout: 12000 };
    }
    return { timeout: 12000 };
  };

  const getNetworkErrorMessage = (feature: string) =>
    `${feature} service is temporarily unreachable. Please ensure backend is running on port 8080 and retry.`;

  useEffect(() => {
    if (!id) {
      return;
    }

    setClubError(null);
    fetchClubDetails();
    fetchClubEvents();
    if (token) {
      fetchClubChannels();
    }
  }, [id]);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'community') {
      setActiveTab('community');
    }
  }, [location.search]);

  const fetchClubEvents = async () => {
    if (!id) {
      return;
    }

    try {
      setEventsLoading(true);
      setClubError(null);
      const endpoint = token
        ? `${API_URL}/api/events/club/${id}`
        : `${API_URL}/public/events/club/${id}`;
      const config = token ? buildConfig(true) : buildConfig(false);
      const response = await axios.get(endpoint, config);
      setClubEvents(response.data);
    } catch (error: any) {
      console.error('Failed to fetch club events:', error);
      if (!error?.response) {
        setClubError(getNetworkErrorMessage('Events'));
      }
      setClubEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchClubDetails = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      setClubError(null);
      console.log('Fetching club details for ID:', id);
      console.log('Token available:', !!token);
      
      // Try the details endpoint first, then fall back to regular endpoint
      const detailsEndpoint = token 
        ? `${API_URL}/api/clubs/${id}`
        : `${API_URL}/public/clubs/${id}`;
      
      console.log('Using endpoint:', detailsEndpoint);
      
      const config = token ? buildConfig(true) : buildConfig(false);
      
      const response = await axios.get(detailsEndpoint, config);
      console.log('Club data fetched successfully:', response.data);
      const clubData = response.data;
      
      setClub(clubData);
      setTitle(clubData.title || '');
      setFacultyMembers(clubData.faculty_members || []);
      setGallery(clubData.gallery || []);
    } catch (error: any) {
      console.error('Failed to fetch club details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      if (!error?.response) {
        setClubError(getNetworkErrorMessage('Club details'));
        setClub(null);
        return;
      }
      
      // Try alternative endpoint if first one fails
      if (error.response?.status === 404) {
        try {
          console.log('Trying alternative endpoint...');
          const altEndpoint = `${API_URL}/api/clubs/${id}`;
          const config = token ? buildConfig(true) : buildConfig(false);
          const altResponse = await axios.get(altEndpoint, config);
          console.log('Club data fetched from alternative endpoint:', altResponse.data);
          setClub(altResponse.data);
          setTitle(altResponse.data.title || '');
          setFacultyMembers(altResponse.data.faculty_members || []);
          setGallery(altResponse.data.gallery || []);
        } catch (altError) {
          console.error('Alternative endpoint also failed:', altError);
          setClubError('Failed to load club details. Please retry.');
          setClub(null);
        }
      } else {
        setClubError(`Failed to load club details. ${error.response?.data?.error || error.message}`);
        setClub(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClubChannels = async () => {
    if (!id || !token) {
      return;
    }

    try {
      setChannelsLoading(true);
      const response = await axios.get(`${API_URL}/api/clubs/${id}/channels`, buildConfig(true));
      const channels = Array.isArray(response.data) ? response.data : [];
      setClubChannels(channels);

      if (channels.length > 0) {
        const selectedExists = selectedChannelId && channels.some((channel: ClubChannel) => channel.id === selectedChannelId);
        const channelId = selectedExists ? selectedChannelId : channels[0].id;
        if (channelId) {
          setSelectedChannelId(channelId);
          await sendPresenceHeartbeat(channelId);
          await Promise.all([
            fetchChannelMessages(channelId),
            fetchChannelMembers(channelId),
            fetchOnlineUsers(channelId)
          ]);
        }
      } else {
        setSelectedChannelId(null);
        setChannelMessages([]);
        setChannelMembers([]);
        setOnlineUsers([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch club channels:', error);
      if (!error?.response) {
        setCommunityStatus(getNetworkErrorMessage('Community channels'));
      } else {
        setCommunityStatus('Unable to load channels for this club.');
      }
      setClubChannels([]);
      setSelectedChannelId(null);
      setOnlineUsers([]);
    } finally {
      setChannelsLoading(false);
    }
  };

  const fetchChannelMessages = async (channelId: string, page: number = 0) => {
    if (!channelId || !token) {
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/channels/${channelId}/messages`, {
        ...buildConfig(true),
        params: { page, size: 50 }
      });
      
      const data = response.data;
      const messages = Array.isArray(data.messages) ? data.messages : [];
      
      if (page === 0) {
        // Replace messages on initial load (newest first, then reverse for display)
        setChannelMessages(messages.reverse());
      } else {
        // Prepend older messages to the top
        setChannelMessages((prev) => [...messages.reverse(), ...prev]);
      }
      
      return data; // Return pagination metadata for infinite scroll
    } catch (error) {
      console.error('Failed to fetch channel messages:', error);
      if (page === 0) setChannelMessages([]);
      return null;
    }
  };

  const loadMoreMessages = async () => {
    if (!selectedChannelId || isLoadingMoreMessagesRef.current) return;
    
    isLoadingMoreMessagesRef.current = true;
    try {
      const data = await fetchChannelMessages(selectedChannelId, paginationPageRef.current + 1);
      if (data && data.hasNext) {
        paginationPageRef.current += 1;
      }
    } finally {
      isLoadingMoreMessagesRef.current = false;
    }
  };

  const fetchChannelMembers = async (channelId: string) => {
    if (!channelId || !token) {
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/channels/${channelId}/members`, buildConfig(true));
      const members = Array.isArray(response.data) ? response.data : [];
      setChannelMembers(members);

      const hasJoined = members.some((member: ClubChannelMember) => member.id === user?.id);
      setJoinedChannelIds((prev) => {
        if (hasJoined && !prev.includes(channelId)) {
          return [...prev, channelId];
        }
        return hasJoined ? prev : prev.filter((id) => id !== channelId);
      });
    } catch (error) {
      console.error('Failed to fetch channel members:', error);
      setChannelMembers([]);
    }
  };

  const fetchOnlineUsers = async (channelId: string) => {
    if (!channelId || !token) {
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/channels/${channelId}/online-users`, buildConfig(true));
      const users = Array.isArray(response.data) ? response.data : [];
      setOnlineUsers(users);
    } catch (error) {
      console.error('Failed to fetch online users:', error);
      setOnlineUsers([]);
    }
  };

  const sendPresenceHeartbeat = async (channelId: string) => {
    if (!channelId || !token) {
      return;
    }

    try {
      await axios.post(`${API_URL}/api/channels/${channelId}/presence`, {}, buildConfig(true));
    } catch (error) {
      console.error('Failed to send presence heartbeat:', error);
    }
  };

  const handleCreateChannel = async () => {
    if (!id || !token) {
      setCommunityStatus('Login required to create channels.');
      return;
    }

    const channelName = newChannelName.trim();
    if (!channelName) {
      setCommunityStatus('Enter a channel name first.');
      return;
    }

    try {
      setChannelActionLoading(true);
      const response = await axios.post(
        `${API_URL}/api/clubs/${id}/channels`,
        { name: channelName },
        buildConfig(true)
      );

      setNewChannelName('');
      setCommunityStatus('Channel created successfully.');
      if (response.data?.id) {
        setSelectedChannelId(response.data.id);
      }
      await fetchClubChannels();
    } catch (error: any) {
      console.error('Failed to create channel:', error);
      setCommunityStatus(error?.response?.data?.error || 'Failed to create channel.');
    } finally {
      setChannelActionLoading(false);
    }
  };

  const handleJoinChannel = async (channelId: string) => {
    if (!token) {
      setCommunityStatus('Login required to join channels.');
      return;
    }

    try {
      setChannelActionLoading(true);
      await axios.post(`${API_URL}/api/channels/${channelId}/join`, {}, buildConfig(true));
      setJoinedChannelIds((prev) => (prev.includes(channelId) ? prev : [...prev, channelId]));
      setCommunityStatus('Joined channel successfully.');
      setSelectedChannelId(channelId);
      await sendPresenceHeartbeat(channelId);
      await fetchChannelMembers(channelId);
      await fetchOnlineUsers(channelId);
      await fetchChannelMessages(channelId);
      await fetchClubChannels();
    } catch (error: any) {
      console.error('Failed to join channel:', error);
      setCommunityStatus(error?.response?.data?.error || 'Failed to join channel.');
    } finally {
      setChannelActionLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedChannelId || !token) {
      setCommunityStatus('Select and join a channel first.');
      return;
    }

    const content = newMessage.trim();
    if (!content) {
      return;
    }

    try {
      setChannelActionLoading(true);

      if (!joinedChannelIds.includes(selectedChannelId)) {
        await handleJoinChannel(selectedChannelId);
      }

      const response = await axios.post(
        `${API_URL}/api/channels/${selectedChannelId}/messages`,
        { content },
        buildConfig(true)
      );

      // Add the message directly from server response
      const newMessageObj = response.data as ClubChannelMessage;
      setChannelMessages((prev) => {
        // Prevent duplicates if WebSocket already added it
        if (prev.some((msg) => msg.id === newMessageObj.id)) {
          return prev;
        }
        return [...prev, newMessageObj];
      });

      setNewMessage('');
      await sendPresenceHeartbeat(selectedChannelId);
      await fetchOnlineUsers(selectedChannelId);
    } catch (error: any) {
      console.error('Failed to send channel message:', error);
      setCommunityStatus(error?.response?.data?.error || 'Failed to send message.');
    } finally {
      setChannelActionLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedChannelId || !token) {
      return;
    }

    // Reset pagination state when channel changes
    paginationPageRef.current = 0;
    isLoadingMoreMessagesRef.current = false;

    // Initial fetch
    sendPresenceHeartbeat(selectedChannelId);
    fetchChannelMessages(selectedChannelId, 0);
    fetchChannelMembers(selectedChannelId);
    fetchOnlineUsers(selectedChannelId);

    // Presence heartbeat every 30 seconds (only when community tab is active)
    presenceIntervalRef.current = setInterval(() => {
      sendPresenceHeartbeat(selectedChannelId);
    }, 30000);

    // Subscribe to new messages via WebSocket (replaces polling!)
    const unsubscribeMessages = subscribe(`/topic/channel/${selectedChannelId}/messages`, (newMessage: ClubChannelMessage) => {
      setChannelMessages((prev) => {
        // Prevent duplicates - check if message already exists
        if (prev.some((msg) => msg.id === newMessage.id)) {
          return prev;
        }
        return [...prev, newMessage];
      });
    });

    // Throttled scroll handler for better performance (100ms throttle)
    const handleScrollThrottled = throttle(() => {
      const container = chatContainerRef.current;
      if (!container || isLoadingMoreMessagesRef.current) return;

      // If scrolled near top (within 100px), load more messages
      if (container.scrollTop < 100) {
        loadMoreMessages();
      }
    }, 100);

    const container = chatContainerRef.current;
    container?.addEventListener('scroll', handleScrollThrottled);

    // Cleanup
    return () => {
      if (presenceIntervalRef.current) {
        clearInterval(presenceIntervalRef.current);
      }
      unsubscribeMessages?.();
      container?.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [selectedChannelId, token, subscribe]);

  useEffect(() => {
    if (id && token) {
      fetchClubChannels();
    }
  }, [token]);

  const handleSaveTemplate = async () => {
    try {
      await axios.put(
        `${API_URL}/api/clubs/${id}/template`,
        {
          title,
          faculty_members: facultyMembers,
          gallery,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setIsEditing(false);
      fetchClubDetails();
      alert('Club template updated successfully!');
    } catch (error) {
      console.error('Failed to update club template', error);
      alert('Failed to update club template');
    }
  };

  const handleAddFaculty = () => {
    if (newFaculty.trim()) {
      setFacultyMembers([...facultyMembers, newFaculty.trim()]);
      setNewFaculty('');
    }
  };

  const handleRemoveFaculty = (index: number) => {
    setFacultyMembers(facultyMembers.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const uploadedUrl = await uploadImageToCdn(file, token, 'club-gallery');
        setGallery([...gallery, uploadedUrl]);
      } catch (error: any) {
        alert(error?.message || 'Failed to upload image');
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setGallery(gallery.filter((_, i) => i !== index));
  };

  const handleBackToClubs = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="club-template-container">
        <motion.button
          onClick={handleBackToClubs}
          className="back-button"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          ← BACK TO CLUBS
        </motion.button>
        <div className="loading-spinner">Loading club details...</div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="club-template-container">
        <motion.button
          onClick={handleBackToClubs}
          className="back-button"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          ← BACK TO CLUBS
        </motion.button>
        <div className="error-message">
          <p>{clubError || 'Club not found or failed to load.'}</p>
          <p style={{ fontSize: '0.9rem', marginTop: '1rem', opacity: 0.8 }}>
            Please check the browser console (F12) for more details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="club-template-container"
    >
      {/* Back Button */}
      <motion.button
        onClick={handleBackToClubs}
        className="back-button"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        ← BACK TO CLUBS
      </motion.button>

      {/* Club Header */}
      <motion.div
        className="club-header"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="club-header-content">
          <h1 className="club-name">{club.name}</h1>
          <p className="club-description">{club.description}</p>
        </div>
        {canEdit && activeTab === 'about' && (
          <motion.button
            className={`edit-toggle-btn ${isEditing ? 'editing' : ''}`}
            onClick={() => setIsEditing(!isEditing)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isEditing ? <><CloseIcon size={14} /> CANCEL EDIT</> : <><EditIcon size={14} /> EDIT TEMPLATE</>}
          </motion.button>
        )}
      </motion.div>

      {/* Tab Navigation */}
      <motion.div
        className="club-tabs"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.25 }}
      >
        <motion.button
          className={`club-tab-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('events');
            setIsEditing(false);
            navigate(location.pathname, { replace: true });
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          EVENTS
        </motion.button>
        <motion.button
          className={`club-tab-btn ${activeTab === 'community' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('community');
            setIsEditing(false);
            navigate(`${location.pathname}?tab=community`, { replace: true });
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          COMMUNITY
        </motion.button>
        <motion.button
          className={`club-tab-btn ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('about');
            navigate(location.pathname, { replace: true });
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ABOUT
        </motion.button>
      </motion.div>

      {/* ===== EVENTS TAB ===== */}
      <AnimatePresence mode="wait">
        {activeTab === 'events' && (
          <motion.div
            key="events-tab"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="template-section"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="section-title">CLUB EVENTS</h2>
              {eventsLoading ? (
                <div className="empty-state">Loading events...</div>
              ) : clubEvents.length > 0 ? (
                <div className="club-events-grid">
                  {clubEvents.map((event, index) => (
                    <motion.div
                      key={event.id}
                      className="club-event-card"
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: index * 0.08 }}
                      whileHover={{ scale: 1.03, y: -5 }}
                      onClick={() => {
                        const currentPath = window.location.pathname;
                        if (currentPath.includes('/dashboard/club')) {
                          navigate(`/dashboard/club/events/${event.id}`);
                        } else if (currentPath.includes('/dashboard/student')) {
                          navigate(`/dashboard/student/events/${event.id}`);
                        } else if (currentPath.includes('/dashboard/admin')) {
                          navigate(`/dashboard/admin/events/${event.id}`);
                        } else {
                          navigate(`/events/${event.id}`);
                        }
                      }}
                    >
                      {event.image && (
                        <div className="club-event-card-image">
                          <img src={event.image} alt={event.name} loading="lazy" />
                          <div className="club-event-card-overlay" />
                        </div>
                      )}
                      <div className="club-event-card-content">
                        <span className="club-event-type-badge">
                          {event.type?.toUpperCase() || 'EVENT'}
                        </span>
                        <h3 className="club-event-card-title">{event.name}</h3>
                        <p className="club-event-card-desc">
                          {event.description?.length > 100
                            ? event.description.substring(0, 100) + '...'
                            : event.description}
                        </p>
                        <div className="club-event-card-meta">
                          {event.date && (
                            <span className="club-event-date">
                              <CalendarIcon size={13} /> {new Date(event.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          )}
                          {event.fee !== undefined && (
                            <span className="club-event-fee">
                              {event.fee > 0 ? `₹${event.fee}` : 'FREE'}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No events registered under this club yet</div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* ===== COMMUNITY TAB ===== */}
        {activeTab === 'community' && (
          <motion.div
            key="community-tab"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="template-section"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="section-title">GROUP CHANNELS</h2>

              {communityStatus && <p className="gc-status-text">{communityStatus}</p>}

              <div className="gc-layout-grid">
                <aside className="gc-panel gc-channels-panel">
                  <h3 className="gc-panel-title">CHANNELS</h3>
                  {channelsLoading ? (
                    <p className="gc-empty-text">Loading channels...</p>
                  ) : clubChannels.length === 0 ? (
                    <p className="gc-empty-text">No channels yet. Create one to start chatting.</p>
                  ) : (
                    <div className="gc-channel-list">
                      {clubChannels.map((channel) => {
                        const isSelected = selectedChannelId === channel.id;
                        const isJoined = joinedChannelIds.includes(channel.id);

                        return (
                          <button
                            key={channel.id}
                            className={`gc-channel-item ${isSelected ? 'active' : ''}`}
                            onClick={() => setSelectedChannelId(channel.id)}
                          >
                            <div className="gc-channel-meta">
                              <span className="gc-channel-name">#{channel.name}</span>
                              <span className="gc-channel-members">
                                {(channel.online || 0)} online • {channel.members} joined
                              </span>
                            </div>
                            <span className={`gc-channel-badge ${isJoined ? 'joined' : 'joinable'}`}>
                              {isJoined ? 'JOINED' : 'JOIN'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {canEdit && (
                    <div className="gc-create-row">
                      <input
                        type="text"
                        className="cyber-input gc-create-input"
                        placeholder="new-channel-name"
                        value={newChannelName}
                        onChange={(e) => setNewChannelName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCreateChannel();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="club-template-cyber-button"
                        onClick={handleCreateChannel}
                        disabled={channelActionLoading}
                      >
                        CREATE
                      </button>
                    </div>
                  )}
                </aside>

                <section className="gc-panel gc-chat-panel">
                  <div className="gc-chat-header">
                    <h3 className="gc-panel-title">
                      {selectedChannel ? `#${selectedChannel.name}` : 'Select a channel'}
                    </h3>
                    {selectedChannel && !isInSelectedChannel && (
                      <button
                        type="button"
                        className="club-template-cyber-button gc-join-btn"
                        onClick={() => handleJoinChannel(selectedChannel.id)}
                        disabled={channelActionLoading}
                      >
                        JOIN CHANNEL
                      </button>
                    )}
                  </div>

                  <div className="gc-messages-box" ref={chatContainerRef} style={{ overflowY: 'auto', maxHeight: '500px' }}>
                    {!selectedChannel && <p className="gc-empty-text">Choose a channel from the left panel.</p>}
                    {selectedChannel && channelMessages.length === 0 && (
                      <p className="gc-empty-text">No messages yet. Be the first to start the discussion.</p>
                    )}
                    {selectedChannel && channelMessages.map((message) => (
                      <div key={message.id} className="gc-message-item">
                        <div className="gc-message-topline">
                          <span className="gc-message-sender">{message.senderName || 'Member'}</span>
                          <span className="gc-message-time">
                            {message.createdAt
                              ? new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : '--:--'}
                          </span>
                        </div>
                        <p className="gc-message-text">{message.content}</p>
                      </div>
                    ))}
                    {isLoadingMoreMessagesRef.current && (
                      <p className="gc-empty-text" style={{ textAlign: 'center', padding: '10px' }}>Loading older messages...</p>
                    )}
                  </div>

                  <div className="gc-composer-row">
                    <input
                      type="text"
                      className="cyber-input gc-message-input"
                      placeholder={selectedChannel ? 'Type message...' : 'Select a channel first'}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={!selectedChannel || !isInSelectedChannel || channelActionLoading}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="club-template-cyber-button"
                      onClick={handleSendMessage}
                      disabled={!selectedChannel || !isInSelectedChannel || channelActionLoading}
                    >
                      SEND
                    </button>
                  </div>
                  {!isInSelectedChannel && selectedChannel && (
                    <p className="gc-empty-text">Join this channel to chat with other members.</p>
                  )}
                </section>

                <aside className="gc-panel gc-members-panel">
                  <h3 className="gc-panel-title">ONLINE NOW</h3>
                  {!selectedChannel && <p className="gc-empty-text">Select a channel to view online users.</p>}
                  {selectedChannel && onlineUsers.length === 0 && (
                    <p className="gc-empty-text">No students online in this channel right now.</p>
                  )}
                  {selectedChannel && onlineUsers.length > 0 && (
                    <div className="gc-member-list">
                      {onlineUsers.map((member) => (
                        <div key={`${selectedChannel.id}-${member.id}`} className="gc-member-row">
                          <span className="gc-member-dot" />
                          <span className="gc-member-name">
                            {member.username}{member.username === currentUsername ? ' (You)' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </aside>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* ===== ABOUT TAB ===== */}
        {activeTab === 'about' && (
          <motion.div
            key="about-tab"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
          >
            {/* Club Title Section */}
            <motion.div
              className="template-section"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="section-title">CLUB TITLE</h2>
              {isEditing ? (
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter club title..."
                  className="cyber-input title-input"
                />
              ) : (
                <div className="title-display">
                  {title || <span className="empty-state">No title set</span>}
                </div>
              )}
            </motion.div>

            {/* Faculty Members Section */}
            <motion.div
              className="template-section"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="section-title">FACULTY MEMBERS</h2>
              {isEditing && (
                <div className="add-faculty-container">
                  <input
                    type="text"
                    value={newFaculty}
                    onChange={(e) => setNewFaculty(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddFaculty()}
                    placeholder="Enter faculty name..."
                    className="cyber-input"
                  />
                  <motion.button
                    onClick={handleAddFaculty}
                    className="club-template-cyber-button add-btn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    + ADD
                  </motion.button>
                </div>
              )}
              <div className="faculty-list">
                <AnimatePresence>
                  {facultyMembers.length > 0 ? (
                    facultyMembers.map((faculty, index) => (
                      <motion.div
                        key={index}
                        className="faculty-card"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <span className="faculty-name">{faculty}</span>
                        {isEditing && (
                          <motion.button
                            onClick={() => handleRemoveFaculty(index)}
                            className="remove-btn"
                            whileHover={{ scale: 1.2, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <CloseIcon size={12} />
                          </motion.button>
                        )}
                      </motion.div>
                    ))
                  ) : (
                    <div className="empty-state">No faculty members added</div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Gallery Section */}
            <motion.div
              className="template-section"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <h2 className="section-title">GALLERY</h2>
              {isEditing && (
                <div className="file-upload-container">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="file-input"
                    id="gallery-upload"
                  />
                  <label htmlFor="gallery-upload" className="file-upload-label">
                    <span className="upload-icon"><FolderIcon size={18} /></span>
                    <span>UPLOAD IMAGE</span>
                  </label>
                </div>
              )}

              {/* Gallery Management for Editing */}
              {isEditing && gallery.length > 0 && (
                <div className="gallery-grid">
                  <AnimatePresence>
                    {gallery.map((image, index) => (
                      <motion.div
                        key={index}
                        className="gallery-item-edit"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <img src={image} alt={`Gallery ${index + 1}`} />
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(index);
                          }}
                          className="remove-image-btn"
                          whileHover={{ scale: 1.2, rotate: 90 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <CloseIcon size={12} />
                        </motion.button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}

              {/* Rolling Gallery for Viewing */}
              {!isEditing && gallery.length > 0 && (
                <div className="rolling-gallery-wrapper">
                  <CurvedSlider
                    images={gallery}
                    speed={30}
                    gap={10}
                    curve={12}
                    reverse={false}
                  />
                </div>
              )}

              {!isEditing && gallery.length === 0 && (
                <div className="empty-state">No images in gallery</div>
              )}
            </motion.div>

            {/* Save Button (only shown when editing) */}
            {isEditing && (
              <motion.div
                className="save-container"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
              >
                <motion.button
                  onClick={handleSaveTemplate}
                  className="club-template-cyber-button save-btn"
                  whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(0, 255, 255, 0.8)' }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SaveIcon size={16} /> SAVE CHANGES
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={selectedImage} alt="Full size" />
              <motion.button
                className="lightbox-close"
                onClick={() => setSelectedImage(null)}
                whileHover={{ scale: 1.2, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <CloseIcon size={16} />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ClubTemplate;
